// ============================================================
// frontend/src/pages/NotesCredit.js
// Gestion des notes de crédit — parité Admin\Factures
// Statut paiement · Peppol B2B · Couleur ambre industriel
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import theme from '../theme';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Couleur thématique Note de Crédit (ambre industriel MKW) ─────────────────
const NC = {
  accent:  '#b45309',   // ambre foncé — texte, icônes
  accentLt:'#fffbeb',   // fond très pâle
  accentMd:'#fef3c7',   // fond badge / header table
  accentBd:'#fde68a',   // bordure badge
};

const S = {
  btn: {
    border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px',
    fontSize: '12px', padding: '6px 11px', transition: 'opacity .15s',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: 'white', borderRadius: '14px', padding: '28px',
    maxWidth: '560px', width: '96%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
    maxHeight: '90vh', overflowY: 'auto',
  },
  input: {
    width: '100%', padding: '9px 12px', border: `1px solid ${theme.border}`,
    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
    background: '#fafafa', color: theme.textPrimary, outline: 'none',
  },
};

// ── Badge statut paiement ─────────────────────────────────────────────────────
function BadgeStatut({ statut }) {
  const cfg = {
    en_attente: { bg: NC.accentMd, color: NC.accent,   border: NC.accentBd, label: '⏳ En attente' },
    traitee:    { bg: '#dcfce7',   color: '#15803d',   border: '#bbf7d0',   label: '✅ Traitée'    },
  };
  const s = cfg[statut] || cfg.en_attente;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: '20px', padding: '3px 10px', fontSize: '11px',
      fontWeight: '700', whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

// ── Badge Peppol ──────────────────────────────────────────────────────────────
function BadgePeppol({ envoyee, date }) {
  if (!envoyee) return null;
  return (
    <span style={{
      background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe',
      borderRadius: '20px', padding: '3px 9px', fontSize: '11px', fontWeight: '700',
    }}>
      🌐 Peppol {date ? new Date(date).toLocaleDateString('fr-BE') : ''}
    </span>
  );
}

export default function NotesCredit() {
  const isMobile = useIsMobile();
  const token   = localStorage.getItem('token');
  const user    = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user?.role === 'admin';
  const config  = { headers: { Authorization: `Bearer ${token}` } };

  const [notes,     setNotes]     = useState([]);
  const [factures,  setFactures]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [form, setForm] = useState({ id_facture: '', montant: '', motif: '', taux_tva: '21' });

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/notes-credit`, config);
      setNotes(res.data.data || []);
    } catch (e) { console.warn('[NotesCredit]', e.message); }
    finally { setLoading(false); }
  }, [token]);

  const fetchFactures = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/factures`, config);
      setFactures(res.data.data || []);
    } catch (e) { console.warn('[Factures pour NC]', e.message); }
  }, [token]);

  useEffect(() => {
    fetchNotes();
    if (isAdmin) fetchFactures();
  }, [fetchNotes, fetchFactures]);

  // ── Créer ─────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setError('');
    if (!form.id_facture) return setError('Sélectionnez une facture.');
    if (!form.montant || parseFloat(form.montant) <= 0) return setError('Montant invalide.');
    if (!form.motif.trim()) return setError('Motif obligatoire.');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/notes-credit`, {
        id_facture: parseInt(form.id_facture),
        montant:    parseFloat(form.montant),
        motif:      form.motif.trim(),
        taux_tva:   parseFloat(form.taux_tva) >= 0 ? parseFloat(form.taux_tva) : 21,
      }, config);
      setShowModal(false);
      setForm({ id_facture: '', montant: '', motif: '', taux_tva: '21' });
      await fetchNotes();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally { setSaving(false); }
  };

  // ── Marquer traitée ───────────────────────────────────────────────────────
  const marquerTraitee = async (id, statutActuel) => {
    const newStatut = statutActuel === 'traitee' ? 'en_attente' : 'traitee';
    const label = newStatut === 'traitee' ? 'traitée (remboursée/déduite)' : 'en attente';
    if (!window.confirm(`Marquer cette note comme ${label} ?`)) return;
    try {
      await axios.patch(`${API_URL}/notes-credit/${id}/statut`, { statut_paiement: newStatut }, config);
      await fetchNotes();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.message || e.message)); }
  };

  // ── Envoyer Peppol ────────────────────────────────────────────────────────
  const envoyerPeppol = async (n) => {
    if (!window.confirm(`Envoyer la note ${n.numero_note_credit} via le réseau Peppol B2B ?`)) return;
    try {
      const res = await axios.post(`${API_URL}/notes-credit/${n.id_note_credit}/peppol`, {}, config);
      if (res.data.already_sent) {
        alert(`ℹ️ ${res.data.message}`);
      } else {
        alert(`✅ Note envoyée via Peppol !\nID Peppyrus : ${res.data.data?.peppyrus_id || '—'}`);
      }
      await fetchNotes();
    } catch (e) { alert('Erreur Peppol : ' + (e.response?.data?.message || e.message)); }
  };

  // ── PDF ───────────────────────────────────────────────────────────────────
  const downloadPDF = async (id, numero) => {
    try {
      const res = await axios.get(`${API_URL}/notes-credit/${id}/pdf`, {
        ...config, responseType: 'blob'
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `NC_${numero}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Erreur PDF : ' + (e.response?.data?.message || e.message)); }
  };

  // ── XML UBL ───────────────────────────────────────────────────────────────
  const downloadXML = async (id, numero) => {
    try {
      const res = await axios.get(`${API_URL}/notes-credit/${id}/xml`, {
        ...config, responseType: 'blob'
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `NC_${numero}_UBL.xml`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Erreur XML : ' + (e.response?.data?.message || e.message)); }
  };

  const supprimerNote = async (id) => {
    if (!window.confirm('Supprimer cette note de crédit ? Action irréversible.')) return;
    try {
      await axios.delete(`${API_URL}/notes-credit/${id}`, config);
      await fetchNotes();
    } catch (e) { alert('Erreur : ' + (e.response?.data?.message || e.message)); }
  };

  // ── Filtres ───────────────────────────────────────────────────────────────
  const filtered = notes.filter(n => {
    const hay = [n.numero_note_credit, n.motif, n.numero_facture,
                 n.client_nom, n.client_prenom].join(' ').toLowerCase();
    const matchSearch  = hay.includes(search.toLowerCase());
    const matchStatut  = !filtreStatut || n.statut_paiement === filtreStatut;
    return matchSearch && matchStatut;
  });

  const nbEnAttente = notes.filter(n => n.statut_paiement === 'en_attente').length;
  const totalAvoirs = notes.reduce((s, n) => {
    const ht  = parseFloat(n.montant) || 0;
    const tva = parseFloat(n.taux_tva) >= 0 ? parseFloat(n.taux_tva) : 21;
    return s + ht * (1 + tva / 100);
  }, 0);

  // ── RENDU ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: theme.pageBg }}>

      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${NC.accent} 0%, #92400e 100%)`,
        padding: '20px 24px', color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700' }}>
              📉 Notes de crédit
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.85 }}>
              {notes.length} note(s) · {nbEnAttente > 0 ? `${nbEnAttente} en attente · ` : ''}
              Total avoirs : {totalAvoirs.toFixed(2)} €
            </p>
          </div>
          {isAdmin && (
            <button onClick={() => { setShowModal(true); setError(''); }}
              style={{ ...S.btn, background: 'white', color: NC.accent,
                fontSize: '14px', padding: '10px 18px' }}>
              ➕ Nouvelle note de crédit
            </button>
          )}
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}>

        {/* Barre de filtres */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Rechercher par numéro, motif, facture, client…"
            style={{ ...S.input, maxWidth: '360px' }} />
          <select value={filtreStatut} onChange={e => setFiltreStatut(e.target.value)}
            style={{ ...S.input, maxWidth: '200px' }}>
            <option value="">Tous les statuts</option>
            <option value="en_attente">⏳ En attente</option>
            <option value="traitee">✅ Traitée</option>
          </select>
        </div>

        {/* KPI bandeaux */}
        {isAdmin && nbEnAttente > 0 && (
          <div style={{ background: NC.accentLt, border: `1px solid ${NC.accentBd}`,
            borderRadius: '10px', padding: '12px 18px', marginBottom: '16px',
            color: NC.accent, fontWeight: '600', fontSize: '14px' }}>
            ⚠️ {nbEnAttente} note{nbEnAttente > 1 ? 's' : ''} de crédit en attente de traitement
            (remboursement ou déduction sur prochaine facture)
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: theme.textSecondary }}>
            ⏳ Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: theme.textSecondary }}>
            {search || filtreStatut ? 'Aucun résultat pour ces filtres.' : 'Aucune note de crédit.'}
          </div>
        ) : isMobile ? (
          /* ── Mobile : cards ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filtered.map(n => {
              const ht  = parseFloat(n.montant) || 0;
              const tva = parseFloat(n.taux_tva) >= 0 ? parseFloat(n.taux_tva) : 21;
              const ttc = ht * (1 + tva / 100);
              const isPro = !!n.client_tva;
              return (
                <div key={n.id_note_credit} style={{
                  background: 'white', borderRadius: '12px',
                  boxShadow: theme.shadow, padding: '12px 14px',
                  borderLeft: `3px solid ${NC.accent}` }}>
                  {/* Ligne 1 : Numéro + Montant TTC */}
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', marginBottom: '6px' }}>
                    <div>
                      <div style={{ fontWeight: '700', color: NC.accent, fontSize: '14px' }}>
                        {n.numero_note_credit}
                      </div>
                      {n.envoyee_peppol && (
                        <BadgePeppol envoyee={n.envoyee_peppol} date={n.date_envoi_peppol} />
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: '800', fontSize: '16px', color: NC.accent }}>
                        − {ttc.toFixed(2)} €
                      </div>
                      <div style={{ fontSize: '11px', color: theme.textSecondary }}>
                        HT: {ht.toFixed(2)} · TVA {tva}%
                      </div>
                    </div>
                  </div>
                  {/* Ligne 2 : Client + Facture */}
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    fontSize: '12px', marginBottom: '5px' }}>
                    <span style={{ fontWeight: '600', color: theme.textPrimary }}>
                      {n.client_nom} {n.client_prenom}
                      {isPro && (
                        <span style={{ marginLeft: '5px', fontSize: '10px',
                          color: '#1d4ed8', fontWeight: '700' }}>B2B</span>
                      )}
                    </span>
                    <span style={{ color: theme.info, flexShrink: 0, marginLeft: '8px' }}>
                      {n.numero_facture || '—'}
                    </span>
                  </div>
                  {/* Ligne 3 : Motif */}
                  {n.motif && (
                    <div style={{ fontSize: '11px', color: theme.textSecondary,
                      marginBottom: '6px', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.motif?.length > 60 ? n.motif.substring(0, 60) + '…' : n.motif}
                    </div>
                  )}
                  {/* Ligne 4 : Date + Statut */}
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '11px', color: theme.textSecondary }}>
                      {new Date(n.date_emission).toLocaleDateString('fr-BE')}
                    </span>
                    <BadgeStatut statut={n.statut_paiement || 'en_attente'} />
                  </div>
                  {/* Ligne 5 : Boutons */}
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button onClick={() => downloadPDF(n.id_note_credit, n.numero_note_credit)}
                      style={{ ...S.btn, background: theme.successBg, color: theme.success }}>
                      📥 PDF
                    </button>
                    {isAdmin && (
                      <>
                        {isPro && (
                          <button onClick={() => downloadXML(n.id_note_credit, n.numero_note_credit)}
                            style={{ ...S.btn, background: '#eff6ff', color: '#1d4ed8' }}>
                            📄 XML
                          </button>
                        )}
                        {isPro && !n.envoyee_peppol && (
                          <button onClick={() => envoyerPeppol(n)}
                            style={{ ...S.btn, background: '#dbeafe', color: '#1d4ed8' }}>
                            🌐 Peppol
                          </button>
                        )}
                        <button
                          onClick={() => marquerTraitee(n.id_note_credit, n.statut_paiement)}
                          style={{ ...S.btn,
                            background: n.statut_paiement === 'traitee' ? '#f0fdf4' : NC.accentLt,
                            color:      n.statut_paiement === 'traitee' ? '#15803d' : NC.accent }}>
                          {n.statut_paiement === 'traitee' ? '↩ En attente' : '✅ Traiter'}
                        </button>
                        <button onClick={() => supprimerNote(n.id_note_credit)}
                          style={{ ...S.btn, background: '#fff7ed', color: '#c2410c' }}>
                          🗑
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Desktop : table originale ── */
          <div style={{ background: 'white', borderRadius: '12px',
            boxShadow: theme.shadow, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: NC.accentMd }}>
                  {['Numéro', 'Facture', 'Client', 'Motif', 'Montant TTC', 'Date', 'Statut', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '11px 13px', textAlign: 'left',
                      fontSize: '11px', fontWeight: '700', color: NC.accent,
                      textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((n, i) => {
                  const ht  = parseFloat(n.montant) || 0;
                  const tva = parseFloat(n.taux_tva) >= 0 ? parseFloat(n.taux_tva) : 21;
                  const ttc = ht * (1 + tva / 100);
                  const isPro = !!n.client_tva;
                  return (
                    <tr key={n.id_note_credit}
                      style={{ background: i % 2 === 0 ? 'white' : '#fafafa',
                        borderTop: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '11px 13px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontWeight: '700', color: NC.accent, fontSize: '13px' }}>
                            {n.numero_note_credit}
                          </span>
                          {n.envoyee_peppol && (
                            <BadgePeppol envoyee={n.envoyee_peppol} date={n.date_envoi_peppol} />
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: '13px', color: theme.info }}>
                        {n.numero_facture || '—'}
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: '13px' }}>
                        {n.client_nom} {n.client_prenom}
                        {isPro && (
                          <span style={{ marginLeft: '5px', fontSize: '11px',
                            color: '#1d4ed8', fontWeight: '600' }}>B2B</span>
                        )}
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: '12px',
                        color: theme.textSecondary, maxWidth: '220px' }}>
                        {n.motif?.length > 65 ? n.motif.substring(0, 65) + '…' : n.motif}
                      </td>
                      <td style={{ padding: '11px 13px' }}>
                        <span style={{ fontWeight: '700', color: NC.accent, fontSize: '14px' }}>
                          − {ttc.toFixed(2)} €
                        </span>
                        <div style={{ fontSize: '11px', color: theme.textSecondary }}>
                          HT: {ht.toFixed(2)} · TVA {tva}%
                        </div>
                      </td>
                      <td style={{ padding: '11px 13px', fontSize: '12px',
                        color: theme.textSecondary, whiteSpace: 'nowrap' }}>
                        {new Date(n.date_emission).toLocaleDateString('fr-BE')}
                      </td>
                      <td style={{ padding: '11px 13px' }}>
                        <BadgeStatut statut={n.statut_paiement || 'en_attente'} />
                      </td>
                      <td style={{ padding: '11px 13px' }}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <button onClick={() => downloadPDF(n.id_note_credit, n.numero_note_credit)}
                            style={{ ...S.btn, background: theme.successBg, color: theme.success }}>
                            📥 PDF
                          </button>
                          {isAdmin && (
                            <>
                              {isPro && (
                                <button onClick={() => downloadXML(n.id_note_credit, n.numero_note_credit)}
                                  style={{ ...S.btn, background: '#eff6ff', color: '#1d4ed8' }}>
                                  📄 XML
                                </button>
                              )}
                              {isPro && !n.envoyee_peppol && (
                                <button onClick={() => envoyerPeppol(n)}
                                  style={{ ...S.btn, background: '#dbeafe', color: '#1d4ed8' }}>
                                  🌐 Peppol
                                </button>
                              )}
                              <button
                                onClick={() => marquerTraitee(n.id_note_credit, n.statut_paiement)}
                                title={n.statut_paiement === 'traitee' ? 'Remettre en attente' : 'Marquer comme traitée'}
                                style={{ ...S.btn,
                                  background: n.statut_paiement === 'traitee' ? '#f0fdf4' : NC.accentLt,
                                  color:      n.statut_paiement === 'traitee' ? '#15803d' : NC.accent }}>
                                {n.statut_paiement === 'traitee' ? '↩ En attente' : '✅ Traiter'}
                              </button>
                              <button onClick={() => supprimerNote(n.id_note_credit)}
                                style={{ ...S.btn, background: '#fff7ed', color: '#c2410c' }}>
                                🗑
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Création ──────────────────────────────────────────────────── */}
      {showModal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={S.modal}>
            <h2 style={{ margin: '0 0 6px', fontSize: '18px', color: theme.textPrimary }}>
              📉 Nouvelle note de crédit
            </h2>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: theme.textSecondary }}>
              Corrige un montant trop facturé ou annule partiellement une facture.
            </p>

            {error && (
              <div style={{ background: NC.accentLt, color: NC.accent, padding: '10px 14px',
                borderRadius: '8px', marginBottom: '14px', fontSize: '13px',
                border: `1px solid ${NC.accentBd}` }}>
                ⚠️ {error}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Facture */}
              <div>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                  color: theme.textSecondary, marginBottom: '5px', textTransform: 'uppercase' }}>
                  Facture concernée *
                </label>
                <select value={form.id_facture}
                  onChange={e => {
                    const f = factures.find(f => f.id_facture === parseInt(e.target.value));
                    setForm(p => ({
                      ...p,
                      id_facture: e.target.value,
                      montant:    f ? parseFloat(f.montant_ttc || 0).toFixed(2) : '',
                      // Pré-remplir le taux TVA depuis la facture sélectionnée
                      taux_tva:   f && f.taux_tva != null ? String(f.taux_tva) : p.taux_tva,
                    }));
                  }}
                  style={S.input}>
                  <option value="">— Sélectionner une facture —</option>
                  {factures.map(f => (
                    <option key={f.id_facture} value={f.id_facture}>
                      {f.numero_facture} · {f.Client?.nom || ''} {f.Client?.prenom || ''}
                      · {parseFloat(f.montant_ttc || 0).toFixed(2)} €
                      {f.Client?.numero_tva ? ' · B2B ✓' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Montant */}
              <div>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                  color: theme.textSecondary, marginBottom: '5px', textTransform: 'uppercase' }}>
                  Montant à créditer (€ HT) *
                </label>
                <input type="number" step="0.01" min="0.01" value={form.montant}
                  onChange={e => setForm(p => ({ ...p, montant: e.target.value }))}
                  placeholder="Ex: 100.00"
                  style={S.input} />
              </div>

              {/* Taux TVA */}
              <div>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                  color: theme.textSecondary, marginBottom: '5px', textTransform: 'uppercase' }}>
                  Taux TVA
                </label>
                <select
                  value={['0','6','12','21'].includes(form.taux_tva) ? form.taux_tva : '_autre'}
                  onChange={e => setForm(p => ({ ...p, taux_tva: e.target.value === '_autre' ? '' : e.target.value }))}
                  style={{ ...S.input, marginBottom: ['0','6','12','21'].includes(form.taux_tva) ? 0 : '8px' }}>
                  <option value="0">0% — Art. 51§2 (cocontractant)</option>
                  <option value="6">6% — Taux réduit</option>
                  <option value="12">12%</option>
                  <option value="21">21% — Taux normal</option>
                  <option value="_autre">Autre (saisir manuellement)</option>
                </select>
                {!['0','6','12','21'].includes(form.taux_tva) && (
                  // Taux personnalisé — pour annuler exactement un document erroné déjà
                  // émis (ex: facture mixte 0%/21% dont le taux a été mal reconstitué à
                  // l'émission, ex. "1%") : la note de crédit doit reproduire EXACTEMENT
                  // le taux déclaré sur le document original pour l'annuler à zéro, pas
                  // afficher un taux "corrigé" qui laisserait un écart en TVA.
                  <input type="number" step="0.01" min="0" value={form.taux_tva}
                    onChange={e => setForm(p => ({ ...p, taux_tva: e.target.value }))}
                    placeholder="Ex: 1 (pour annuler un taux erroné de 1%)"
                    style={{ ...S.input }} />
                )}
                {form.montant && (
                  <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '5px' }}>
                    → TTC : {(parseFloat(form.montant || 0) * (1 + (parseFloat(form.taux_tva) >= 0 ? parseFloat(form.taux_tva) : 21) / 100)).toFixed(2)} €
                  </div>
                )}
              </div>

              {/* Motif */}
              <div>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '12px',
                  color: theme.textSecondary, marginBottom: '5px', textTransform: 'uppercase' }}>
                  Motif *
                </label>
                <select
                  value={['Erreur de facturation','Annulation partielle','Remise accordée','Retour de marchandise','Doublon de facturation'].includes(form.motif) ? form.motif : '_autre'}
                  onChange={e => setForm(p => ({ ...p, motif: e.target.value === '_autre' ? '' : e.target.value }))}
                  style={{ ...S.input, marginBottom: '8px' }}>
                  <option value="Erreur de facturation">Erreur de facturation</option>
                  <option value="Annulation partielle">Annulation partielle</option>
                  <option value="Remise accordée">Remise accordée après facturation</option>
                  <option value="Retour de marchandise">Retour de marchandise</option>
                  <option value="Doublon de facturation">Doublon de facturation</option>
                  <option value="_autre">Autre (saisir manuellement)</option>
                </select>
                <textarea value={form.motif}
                  onChange={e => setForm(p => ({ ...p, motif: e.target.value }))}
                  placeholder="Précisez le motif complet…" rows={2}
                  style={{ ...S.input, resize: 'vertical' }} />
              </div>

              {/* Info Peppol */}
              {form.id_facture && factures.find(f => f.id_facture === parseInt(form.id_facture))?.Client?.numero_tva && (
                <div style={{ background: '#eff6ff', padding: '10px 14px', borderRadius: '8px',
                  fontSize: '13px', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                  🌐 Client B2B — XML UBL CreditNote Peppol disponible + envoi direct réseau Peppol.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
              <button onClick={handleCreate} disabled={saving}
                style={{ flex: 1, padding: '11px', background: NC.accent, color: 'white',
                  border: 'none', borderRadius: '8px', fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? '⏳ Création…' : '📉 Créer la note de crédit'}
              </button>
              <button onClick={() => setShowModal(false)}
                style={{ padding: '11px 18px', background: '#f0f0f0',
                  color: theme.textSecondary, border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
